import { lazy } from 'react';

/**
 * How each engine words "the chunk you asked for is not there any more".
 *
 * A deploy renames every hashed chunk, so a tab holding the previous
 * index.html asks for a file the server no longer has. What comes back is not
 * a 404 but our SPA fallback — the index.html shell, served as text/html — and
 * each engine complains about a different half of that: Chromium and Firefox
 * about the fetch, WebKit about the MIME type of what arrived. Safari and
 * Chrome on iOS are both WebKit, so the MIME wording is what a phone reports.
 *
 * Exported because Sentry's `ignoreErrors` in main.tsx reads the same list:
 * the reload below is the fix, and an error we fix by reloading is not one to
 * page anyone about. Two copies of these strings drift, and the drift is
 * invisible until a phone hits it in production.
 */
export const STALE_CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module', // Chromium
  'error loading dynamically imported module', // Firefox
  'Importing a module script failed', // WebKit, when the request itself fails
  'is not a valid JavaScript MIME type', // WebKit, when the shell comes back instead
];

/**
 * Wraps React.lazy() to handle stale chunk errors after deploys.
 *
 * When Vite rebuilds, chunk filenames change (content hashing). Users with a
 * cached index.html still reference old chunk URLs that no longer exist. This
 * wrapper detects that error and does a single page reload to fetch fresh HTML.
 */
export function lazyWithReload<T extends React.ComponentType>(
  importFn: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await importFn();
    } catch (error) {
      if (isStaleChunkError(error) && !hasReloadedForChunkError()) {
        markReloadedForChunkError();
        window.location.reload();
        // Return a never-resolving promise so React doesn't render an error
        // while the page reloads
        return new Promise(() => {});
      }
      throw error;
    }
  });
}

const RELOAD_KEY = 'chunk-reload';

export function isStaleChunkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return STALE_CHUNK_ERROR_PATTERNS.some((pattern) => error.message.includes(pattern));
}

// Storage can be absent or throw (Firefox nulls it out, Safari throws when it
// is disabled). It only guards against a reload loop, so losing it costs one
// extra reload; letting it throw here would replace a recoverable chunk error
// with an uncaught one, which is the opposite of the point.
function hasReloadedForChunkError(): boolean {
  try {
    return globalThis.sessionStorage?.getItem(RELOAD_KEY) === 'true';
  } catch {
    return false;
  }
}

function markReloadedForChunkError(): void {
  try {
    globalThis.sessionStorage?.setItem(RELOAD_KEY, 'true');
  } catch {
    // storage unavailable
  }
}
